import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { specialNameService } from '../utils/api';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook for fetching special names with caching
 */
export const useSpecialNames = (schoolId) => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useQuery({
    queryKey: ['specialNames', schoolId, language],
    queryFn: () => specialNameService.getSpecialNames(schoolId, language),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!schoolId, // Only run if schoolId is provided
  });
};

/**
 * Custom hook for fetching a single special name with caching
 */
export const useSpecialName = (specialNameId) => {
  return useQuery({
    queryKey: ['specialName', specialNameId],
    queryFn: () => specialNameService.getSpecialName(specialNameId),
    enabled: !!specialNameId, // Only run if specialNameId exists
  });
};

/**
 * Custom hook for searching special names with caching
 */
export const useSearchSpecialNames = (schoolId, query = '') => {
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';

  return useQuery({
    queryKey: ['specialNamesSearch', schoolId, language, query],
    queryFn: () => specialNameService.searchSpecialNames(schoolId, query, language),
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!schoolId && query.length > 0, // Only run if schoolId exists and query has content
  });
};

/**
 * Custom hook for creating a special name with optimistic updates
 */
export const useCreateSpecialName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newSpecialName) => specialNameService.createSpecialName(newSpecialName),
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to ensure data is fresh
      queryClient.invalidateQueries({
        queryKey: ['specialNames', variables.schoolId],
      });
      
      // If this was a search result, invalidate that too
      if (variables.name) {
        queryClient.invalidateQueries({
          queryKey: ['specialNamesSearch', variables.schoolId],
        });
      }
    },
  });
};

/**
 * Custom hook for updating a special name with optimistic updates
 */
export const useUpdateSpecialName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }) => specialNameService.updateSpecialName(id, data),
    onSuccess: (data, variables) => {
      console.log(`[SpecialNames] Update success for ID ${variables.id}. Response:`, data);
      
      // Handle new completion checker response format
      if (data?.queueUpdates) {
        console.log(`[SpecialNames] Processing queue updates:`, data.queueUpdates);
        
        // Update specific queues based on completion status
        if (data.queueUpdates.removeFromTranslations) {
          queryClient.setQueryData(['missingTranslations'], (oldData) => 
            oldData?.filter(item => item.id !== variables.id) || []
          );
        }
        
        if (data.queueUpdates.removeFromNutrients) {
          queryClient.setQueryData(['missingNutrients'], (oldData) => 
            oldData?.filter(item => item.id !== variables.id) || []
          );
        }
        
        if (data.queueUpdates.removeFromTags) {
          queryClient.setQueryData(['missingTags'], (oldData) => 
            oldData?.filter(item => item.id !== variables.id) || []
          );
        }
        
        // Update special names cache with new data
        queryClient.setQueryData(['specialName', variables.id], (oldData) => ({
          ...oldData,
          translations: data.updatedTranslations || oldData?.translations
        }));
        
      } else {
        // Fallback: use old invalidation approach if new format not available
        console.log(`[SpecialNames] Using fallback cache invalidation for ID ${variables.id}`);
        queryClient.invalidateQueries({
          queryKey: ['specialNames'],
        });
        queryClient.invalidateQueries({
          queryKey: ['specialName', variables.id],
        });
        queryClient.invalidateQueries({
          queryKey: ['specialNamesSearch'],
        });
      }
      
      // Always invalidate general special names list to ensure consistency
      queryClient.invalidateQueries({
        queryKey: ['specialNames'],
      });
    },
  });
};

/**
 * Custom hook for deleting a special name with optimistic updates
 */
export const useDeleteSpecialName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => specialNameService.deleteSpecialName(id),
    onSuccess: () => {
      // Invalidate all special names queries to ensure data is fresh
      queryClient.invalidateQueries({
        queryKey: ['specialNames'],
      });
      // Invalidate search results too
      queryClient.invalidateQueries({
        queryKey: ['specialNamesSearch'],
      });
    },
  });
};

/**
 * Custom hook for replacing a special name and deleting the original
 */
export const useReplaceSpecialName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ specialNameId, replaceWithId }) => 
      specialNameService.replaceSpecialNameAndDelete(specialNameId, replaceWithId),
    onSuccess: () => {
      // Invalidate all special names queries to ensure data is fresh
      queryClient.invalidateQueries({
        queryKey: ['specialNames'],
      });
      // Invalidate search results too
      queryClient.invalidateQueries({
        queryKey: ['specialNamesSearch'],
      });
    },
  });
}; 